import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/index.css';
import reportWebVitals from './reportWebVitals';
import { syncDocumentHeadFromConfig } from './components/SeoHead';
import { applyThemeColorsFromConfig, applyThemeTypographyFromConfig } from './services/clientConfigService';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getBootstrapFirestore } from './firebase/firebaseBootstrapConfig';
import { initAppCheckFromClientConfig } from './firebase/initAppCheck';
import { initFirebaseObservability } from './firebase/initFirebaseObservability';
import { renderBootstrapError } from './bootstrap/renderBootstrapError';
import { getEnvironment, isProductionHostname } from './utils/environmentDetection';

/**
 * Bootstrap: load client config, then load the app.
 *
 * - Client's own project (production): If /config/firebase.json exists, we fetch
 *   clientConfigs/<clientId> from that project. Production config always comes from
 *   the client's Firestore; there is no shared production.
 * - Staging only: If no firebase.json, we use the shared staging project
 *   (unwontedartistry-stage) to fetch config for local/dev staging.
 */

const CONFIG_FETCH_RETRIES = 3;
const CONFIG_FETCH_RETRY_DELAY_MS = 600;

async function getDocWithRetry(
  getDocFn: () => Promise<Awaited<ReturnType<typeof getDoc>>>
): Promise<Awaited<ReturnType<typeof getDoc>>> {
  let lastErr: Error | null = null;
  for (let attempt = 1; attempt <= CONFIG_FETCH_RETRIES; attempt++) {
    try {
      const snapshot = await getDocFn();
      return snapshot;
    } catch (e) {
      lastErr = e instanceof Error ? e : new Error(String(e));
    }
    if (attempt < CONFIG_FETCH_RETRIES) {
      await new Promise((r) => setTimeout(r, CONFIG_FETCH_RETRY_DELAY_MS));
    }
  }
  throw lastErr ?? new Error('Failed to fetch config');
}

async function bootstrap() {
  try {
    // Step 1: Load client ID from minimal config file
    const timestamp = new Date().getTime();
    const clientIdRes = await fetch(`/config/client-id.json?t=${timestamp}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });

    if (!clientIdRes.ok) {
      throw new Error(`Failed to load client-id.json: ${clientIdRes.status}`);
    }

    const clientIdData = await clientIdRes.json();
    // Prefer build-time env so local dev isn't stuck on test-client after running tests
    const clientId = (process.env.REACT_APP_CLIENT_ID || clientIdData.clientId || '').trim();

    if (!clientId) {
      throw new Error('client-id.json missing clientId field');
    }

    // Step 2: Try build-time Firebase config (client's own project)
    let firebaseJson: { projectId: string; apiKey: string; authDomain?: string; storageBucket?: string; appId?: string; measurementId?: string } | null = null;
    try {
      const firebaseRes = await fetch(`/config/firebase.json?t=${timestamp}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' }
      });
      if (firebaseRes.ok) {
        const data = await firebaseRes.json();
        if (data?.projectId && data?.apiKey) {
          firebaseJson = data;
        }
      }
    } catch {
      // No firebase.json
    }

    if (firebaseJson) {
      // Bootstrap from client's own project: all client-varying values must be in firebase.json (no defaults)
      const storageBucket = firebaseJson.storageBucket?.trim();
      const authDomain = firebaseJson.authDomain?.trim();
      if (!storageBucket) {
        throw new Error(
          'firebase.json is missing storageBucket. Client builds must set REACT_APP_FIREBASE_STORAGE_BUCKET (e.g. my-project.firebasestorage.app) so the script writes it. No default.'
        );
      }
      if (!authDomain) {
        throw new Error(
          'firebase.json is missing authDomain. Client builds must set REACT_APP_FIREBASE_AUTH_DOMAIN (e.g. my-project.firebaseapp.com). No default.'
        );
      }
      const app =
        getApps().length > 0
          ? getApp()
          : initializeApp({
              apiKey: firebaseJson.apiKey,
              authDomain,
              projectId: firebaseJson.projectId,
              storageBucket,
              ...(firebaseJson.appId && { appId: firebaseJson.appId }),
              ...(firebaseJson.measurementId && { measurementId: firebaseJson.measurementId }),
            });
      const db = getFirestore(app);
      const configDocRef = doc(db, 'clientConfigs', clientId);
      const configDoc = await getDocWithRetry(() => getDoc(configDocRef));

      if (!configDoc.exists()) {
        throw new Error(`Client config not found in Firestore for client: ${clientId} (project: ${firebaseJson.projectId}). Ensure CMC has pushed config to this project.`);
      }

      const config = configDoc.data() as Record<string, unknown> | undefined;
      if (config?.clientId && config.clientId !== clientId) {
        // no-op; config doc may have different clientId field
      }
      // Ensure clientId and features exist so app code never sees undefined (production doc may be partial)
      const defaultFeatures = { admin: true, hero: true, bookings: false, contracts: false, sms: false, casting: false, availability: false, publications: false, shop: false, models: false, tax: false };
      const mergedConfig = {
        ...config,
        clientId: (config?.clientId as string) || clientId,
        features: typeof config?.features === 'object' && config.features !== null ? { ...defaultFeatures, ...(config.features as Record<string, boolean>) } : defaultFeatures,
      };
      initAppCheckFromClientConfig(app, mergedConfig as Record<string, unknown>);
      void initFirebaseObservability(app);
      (window as any).__CLIENT_CONFIG = mergedConfig;
      applyThemeColorsFromConfig();
      applyThemeTypographyFromConfig();
      syncDocumentHeadFromConfig();

      const rootElement = document.getElementById('root');
      if (!rootElement) {
        throw new Error('Root element not found');
      }
      const root = ReactDOM.createRoot(rootElement);
      const { default: App } = await import('./App');
      root.render(
        <React.StrictMode>
          <App />
        </React.StrictMode>
      );
      reportWebVitals();
      return;
    }

    // Step 3: No firebase.json — require client's project for production; never fall back to staging on production hostnames
    const environment = getEnvironment();
    if (environment === 'production') {
      throw new Error(
        `Production requires the client's Firebase project. No firebase.json found. ` +
        `Build with REACT_APP_FIREBASE_PROJECT_ID and REACT_APP_FIREBASE_API_KEY for the client's project, ` +
        `or for local dev run: REACT_APP_FIREBASE_PROJECT_ID=<projectId> REACT_APP_FIREBASE_API_KEY=<apiKey> npm run start:client -- ${clientId}`
      );
    }

    // Staging-only path: never use staging bootstrap on a known production hostname (avoids wrong project / auth failures)
    if (isProductionHostname()) {
      throw new Error(
        `This domain is a production hostname but the app was built without the client's Firebase config (no firebase.json). ` +
        `Auth and data would use the wrong project. Redeploy with BUILD_MODE=client_project, FIREBASE_PROJECT_ID=<your-production-project>, ` +
        `FIREBASE_API_KEY=<project Web API Key>, and DEPLOY_ENV=production (or unset). See CMC Build & Deploy.`
      );
    }

    // Staging only: platform bootstrap from shared staging project
    const bootstrapDb = getBootstrapFirestore();
    const configDocRef = doc(bootstrapDb, 'clientConfigs', clientId);
    const configDoc = await getDocWithRetry(() => getDoc(configDocRef));

    if (!configDoc.exists()) {
      const hint =
        clientId === 'test-client'
          ? ' For staging run: npm run start:client -- stage-workspace (or another client that exists in Firestore). The test runner overwrites the client ID with "test-client".'
          : '';
      throw new Error(
        `Client config not found in Firestore for client: ${clientId} (staging project: unwontedartistry-stage).${hint}`
      );
    }

    const config = configDoc.data() as Record<string, unknown> | undefined;
    if (config?.clientId && config.clientId !== clientId) {
      // no-op
    }
    const defaultFeaturesStaging = { admin: true, hero: true, bookings: false, contracts: false, sms: false, casting: false, availability: false, publications: false, shop: false, models: false, tax: false };
    const mergedConfigStaging = {
      ...config,
      clientId: (config?.clientId as string) || clientId,
      features: typeof config?.features === 'object' && config.features !== null ? { ...defaultFeaturesStaging, ...(config.features as Record<string, boolean>) } : defaultFeaturesStaging,
    };
    initAppCheckFromClientConfig(getApp(), mergedConfigStaging as Record<string, unknown>);
    void initFirebaseObservability(getApp());
    (window as any).__CLIENT_CONFIG = mergedConfigStaging;
    applyThemeColorsFromConfig();
    applyThemeTypographyFromConfig();
    syncDocumentHeadFromConfig();

    const rootElement = document.getElementById('root');
    if (!rootElement) {
      throw new Error('Root element not found');
    }
    const root = ReactDOM.createRoot(rootElement);
    const { default: App } = await import('./App');
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
    reportWebVitals();
  } catch (err: unknown) {
    const rootElement = document.getElementById('root');
    if (rootElement) {
      renderBootstrapError(rootElement, err);
    }
  }
}

bootstrap();
